import React, { useState, useEffect, useCallback } from 'react';
import { ForecastFieldsContext } from './forecastFieldsTypes';
import forecastFieldsService, { type ForecastField } from '../api/forecastFieldsService';
import { useAuth } from '../hooks/useAuth';
import { isTokenExpired } from '../utils/helpers';

/**
 * Provider that fetches forecast field metadata and shares it with all consumers.
 * Fetches only when the user has a valid session: GET /api/forecast-fields is behind
 * authenticateRequest, so an initial fetch while logged out fails and would leave
 * fields empty until refetch — we run again when auth becomes valid (e.g. after login).
 */
export const ForecastFieldsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState } = useAuth();
  const isValidAuth =
    authState.isAuthenticated &&
    !!authState.token &&
    !isTokenExpired(authState.token);

  const [fields, setFields] = useState<ForecastField[]>([]);
  const [fieldsByDbName, setFieldsByDbName] = useState<Map<string, ForecastField>>(new Map());
  const [loading, setLoading] = useState(isValidAuth);
  const [error, setError] = useState<Error | null>(null);

  const fetchFields = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const fetchedFields = await forecastFieldsService.getAllForecastFields();
      setFields(fetchedFields);
      setFieldsByDbName(new Map(fetchedFields.map(f => [f.db_name, f])));
    } catch (err) {
      console.error('Failed to fetch forecast fields:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isValidAuth) {
      setFields([]);
      setFieldsByDbName(new Map());
      setError(null);
      setLoading(false);
      return;
    }
    fetchFields();
  }, [isValidAuth, fetchFields]);

  return (
    <ForecastFieldsContext.Provider value={{ fields, fieldsByDbName, loading, error, refetch: fetchFields }}>
      {children}
    </ForecastFieldsContext.Provider>
  );
};
